import { useEffect, useRef, useState } from "react";
import { Pause } from "lucide-react";
import { track } from "@/lib/analytics";

/**
 * The Many-You Practice — a single, calm 2-minute inquiry that lives
 * in the Sutra "Practice" section. Five states:
 *   intro → guided (5 steps) → reflection → finish
 *   guided → stop (gentle off-ramp) → intro
 *
 * Wellbeing/stop affordances use the calm blue (#6a8fa8). Reflection
 * note is stored only in localStorage — never sent anywhere.
 */

type Phase = "intro" | "guided" | "reflection" | "finish" | "stop";

const STEPS = [
  "One slow breath in… and a longer breath out.",
  "Bring a parent to mind — to them, you are a child.",
  "Now a close friend — to them, you're a friend. Same you.",
  "Now your work or study — there, a colleague. One you, many forms.",
  "This is what 'one reality, many forms' means. Rest here a moment.",
];

const CALM_BLUE = "#6a8fa8";
const REFLECTION_KEY = "sutra.manyYou.reflection";

export const PracticeCard = () => {
  const [phase, setPhase] = useState<Phase>("intro");
  const [stepIdx, setStepIdx] = useState(0);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (phase === "reflection") {
      try {
        const saved = localStorage.getItem(REFLECTION_KEY);
        if (saved) setNote(saved);
      } catch {
        /* ignore */
      }
    }
  }, [phase]);

  const reset = () => {
    setPhase("intro");
    setStepIdx(0);
  };

  const begin = () => {
    track("practice_started");
    setStepIdx(0);
    setPhase("guided");
  };

  const next = () => {
    if (stepIdx < STEPS.length - 1) {
      setStepIdx(stepIdx + 1);
    } else {
      setPhase("reflection");
    }
  };

  const saveNote = (value: string) => {
    setNote(value);
    try {
      if (value.trim()) localStorage.setItem(REFLECTION_KEY, value);
      else localStorage.removeItem(REFLECTION_KEY);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="relative overflow-hidden rounded-3xl border border-border/70 bg-paper-warm/70 shadow-sm">
      {/* Soft breathing wash behind guided/stop states */}
      {(phase === "guided" || phase === "stop") && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-0"
        >
          <div
            className="absolute left-1/2 top-1/2 h-[120%] w-[120%] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-60 blur-3xl breathe-slow"
            style={{
              background: `radial-gradient(circle, ${CALM_BLUE}22 0%, transparent 65%)`,
            }}
          />
        </div>
      )}

      <div className="relative z-10 px-6 py-10 sm:px-10 sm:py-14">
        {phase === "intro" && <IntroState onBegin={begin} />}
        {phase === "guided" && (
          <GuidedState
            stepIdx={stepIdx}
            onNext={next}
            onStop={() => setPhase("stop")}
          />
        )}
        {phase === "stop" && <StopState onBack={reset} />}
        {phase === "reflection" && (
          <ReflectionState
            note={note}
            onChange={saveNote}
            onFinish={() => {
              track("practice_finished");
              setPhase("finish");
            }}
          />
        )}
        {phase === "finish" && <FinishState onAgain={reset} />}
      </div>

      <style>{`
        @keyframes sutra-breathe {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.45; }
          50% { transform: translate(-50%, -50%) scale(1.08); opacity: 0.7; }
        }
        .breathe-slow { animation: sutra-breathe 8s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .breathe-slow { animation: none; }
        }
      `}</style>
    </div>
  );
};

/* ─────────────────────────── States ─────────────────────────── */

const IntroState = ({ onBegin }: { onBegin: () => void }) => (
  <div className="mx-auto max-w-xl text-center">
    <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">
      Inquiry · 2 min
    </p>
    <h3 className="mt-4 font-display text-3xl font-light tracking-tight text-foreground sm:text-4xl">
      The Many-You Practice
    </h3>
    <p className="mx-auto mt-5 max-w-md text-[15px] leading-relaxed text-ink-soft">
      Feel, in your own life, what "one reality, many forms" actually means.
    </p>
    <p className="mx-auto mt-3 max-w-md text-sm italic text-muted-foreground">
      It's okay if it stays just an idea for now — realization comes in layers.
    </p>

    <p
      className="mx-auto mt-8 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-medium"
      style={{
        color: CALM_BLUE,
        background: `${CALM_BLUE}14`,
      }}
    >
      <span
        aria-hidden="true"
        className="inline-block h-1.5 w-1.5 rounded-full"
        style={{ background: CALM_BLUE }}
      />
      You can stop anytime.
    </p>

    <div className="mt-10">
      <button
        type="button"
        onClick={onBegin}
        className="inline-flex items-center justify-center rounded-full bg-primary px-7 py-3 font-display text-sm font-medium tracking-wide text-primary-foreground shadow-sm transition-all hover:bg-primary/90 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
      >
        Begin
      </button>
    </div>
  </div>
);

interface GuidedProps {
  stepIdx: number;
  onNext: () => void;
  onStop: () => void;
}

const GuidedState = ({ stepIdx, onNext, onStop }: GuidedProps) => {
  const isLast = stepIdx === STEPS.length - 1;
  return (
    <div className="mx-auto max-w-xl">
      {/* Progress */}
      <div
        className="mx-auto mb-10 flex items-center justify-center gap-2"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={STEPS.length}
        aria-valuenow={stepIdx + 1}
        aria-label={`Step ${stepIdx + 1} of ${STEPS.length}`}
      >
        {STEPS.map((_, i) => (
          <span
            key={i}
            aria-hidden="true"
            className="h-1 rounded-full transition-all duration-500"
            style={{
              width: i === stepIdx ? 28 : 16,
              background: i <= stepIdx ? CALM_BLUE : `${CALM_BLUE}33`,
            }}
          />
        ))}
      </div>

      <p
        key={stepIdx}
        className="mx-auto min-h-[6rem] text-center font-display text-2xl font-light leading-snug text-foreground sm:text-3xl"
        style={{ animation: "sutra-fade 700ms ease-out both" }}
      >
        {STEPS[stepIdx]}
      </p>

      <div className="mt-12 flex flex-col items-center gap-5">
        <button
          type="button"
          onClick={onNext}
          className="inline-flex items-center justify-center rounded-full bg-primary px-7 py-3 font-display text-sm font-medium tracking-wide text-primary-foreground shadow-sm transition-all hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
        >
          {isLast ? "Continue" : "Next"}
        </button>

        <button
          type="button"
          onClick={onStop}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2"
          style={{ color: CALM_BLUE }}
        >
          <Pause className="h-3.5 w-3.5" />
          Stop
        </button>
      </div>

      <style>{`
        @keyframes sutra-fade {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

const StopState = ({ onBack }: { onBack: () => void }) => (
  <div className="mx-auto max-w-md text-center">
    <p
      className="text-[11px] uppercase tracking-[0.22em]"
      style={{ color: CALM_BLUE }}
    >
      Paused
    </p>
    <h3 className="mt-4 font-display text-2xl font-light leading-snug text-foreground sm:text-3xl">
      That's completely okay.
    </h3>
    <p className="mx-auto mt-5 max-w-sm text-[15px] leading-relaxed text-ink-soft">
      Stopping when you need to is part of the practice. Feel your feet on the
      floor, take one easy breath.
    </p>
    <div className="mt-9">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center justify-center rounded-full border px-6 py-2.5 text-sm font-medium transition-colors hover:bg-background focus:outline-none focus-visible:ring-2"
        style={{
          color: CALM_BLUE,
          borderColor: `${CALM_BLUE}55`,
        }}
      >
        Back to start
      </button>
    </div>
  </div>
);

interface ReflectionProps {
  note: string;
  onChange: (v: string) => void;
  onFinish: () => void;
}

const ReflectionState = ({ note, onChange, onFinish }: ReflectionProps) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <div className="mx-auto max-w-xl">
      <p className="text-center text-[11px] uppercase tracking-[0.22em] text-ink-soft">
        Reflection
      </p>
      <h3 className="mt-4 text-center font-display text-2xl font-light leading-snug text-foreground sm:text-3xl">
        What did you notice?
      </h3>

      <div className="mx-auto mt-7 max-w-md">
        <textarea
          ref={ref}
          value={note}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          placeholder="A word or two is plenty"
          className="w-full resize-none rounded-2xl border border-border/70 bg-background px-4 py-3 text-[15px] leading-relaxed text-foreground placeholder:text-muted-foreground/70 focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
        <p className="mt-2 text-center text-xs text-muted-foreground">
          Saved only on your device.
        </p>
      </div>

      <div className="mt-8 flex justify-center">
        <button
          type="button"
          onClick={onFinish}
          className="inline-flex items-center justify-center rounded-full bg-primary px-7 py-3 font-display text-sm font-medium tracking-wide text-primary-foreground shadow-sm transition-all hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
        >
          Finish
        </button>
      </div>
    </div>
  );
};

const FinishState = ({ onAgain }: { onAgain: () => void }) => (
  <div className="mx-auto max-w-md text-center">
    <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">
      Complete
    </p>
    <p className="mx-auto mt-5 max-w-sm font-display text-2xl font-light leading-snug text-foreground sm:text-[26px]">
      However that went is fine. You can carry it with you, or just let it go.
    </p>
    <div className="mt-9">
      <button
        type="button"
        onClick={onAgain}
        className="inline-flex items-center justify-center rounded-full border border-border/70 bg-background px-6 py-2.5 text-sm font-medium text-foreground transition-colors hover:border-primary/40 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
      >
        Begin again
      </button>
    </div>
  </div>
);

export default PracticeCard;
